import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSetClientPhoneNumberMutation } from '@app/api/Client/api';
import {
  useCheckClientForDuplicateMutation,
  useMergeClientsAtSameBranchMutation,
} from '@app/api/ClientsMerge';
import type { CheckClientForDuplicateResponse } from '@app/api/ClientsMerge';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { maskRules } from '@app/components/Phone/constants/masks';
import { applyMask, getPhoneDigits } from '@app/components/Phone/utils';
import {
  CLIENT_CARD_PATH,
  FRONT_WARNING,
  LEAD_CARD_PATH,
  SUCCESS,
  WARNING,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useConfirmNew } from '@app/providers/NewConfirmProvider/useConfirmNew';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { selectCountries, useAppSelector } from '@app/store';

interface UseSaveMessagePhoneParams {
  digits: string;
  countryId: number;
}

/** Телефоны клиента в карточке лида показываются как +<цифры>. */
const toPhoneText = (digits: string): string => `+${digits}`;

export const useSaveMessagePhone = ({
  digits,
  countryId,
}: UseSaveMessagePhoneParams) => {
  const { activeBranchId } = useUserPrint();
  const { showNotification, api } = useNotification();
  const { show } = useConfirmNew();
  const { leadId, leadCardData, knownClientId, refetchLeadCard } =
    useLeadCardContext();
  const countries = useAppSelector(selectCountries);
  const navigate = useNavigate();

  const [setClientPhoneNumber] = useSetClientPhoneNumberMutation();
  const [checkClientForDuplicate] = useCheckClientForDuplicateMutation();
  const [mergeClientsAtSameBranch] = useMergeClientsAtSameBranchMutation();

  const [isSaving, setIsSaving] = useState(false);
  const [duplicate, setDuplicate] =
    useState<CheckClientForDuplicateResponse | null>(null);
  const isSavePendingRef = useRef(false);

  // Дубли ищутся в филиале КЛИЕНТА, а не пользователя: по этому филиалу бэк
  // решает, можно ли объединить карточки (us/11255).
  const clientBranchId = leadCardData?.BranchId ?? null;

  // Та же формула, что у действий карточки лида: у объединённых карточек
  // клиент из ответа лида может быть не тем, с которым работает родитель.
  const clientId = useMemo(
    () =>
      knownClientId ??
      leadCardData?.ClientId ??
      leadCardData?.ClientView?.Id ??
      null,
    [knownClientId, leadCardData?.ClientId, leadCardData?.ClientView?.Id]
  );

  const maskedPhone = useMemo(() => {
    const country = countries.find(({ Id }) => Id === countryId);
    const rules = country?.Alpha2 ? maskRules[country.Alpha2] : undefined;

    return rules ? applyMask(digits, rules) : `+${digits}`;
  }, [countries, countryId, digits]);

  const currentPhones = useMemo(
    () => ({
      phone1: getPhoneDigits(leadCardData?.ClientView?.Phone1 ?? ''),
      phone2: getPhoneDigits(leadCardData?.ClientView?.Phone2 ?? ''),
    }),
    [leadCardData?.ClientView?.Phone1, leadCardData?.ClientView?.Phone2]
  );

  const isAlreadySaved =
    currentPhones.phone1 === digits || currentPhones.phone2 === digits;

  // SetClientPhoneNumber всегда делает номер основным и сдвигает прежний
  // основной в дополнительный, затирая тот, что там был, — подтверждено
  // кодом beauty-crm CRM.Business/Clients/LogicClient.cs:118-163. Клиент
  // должен видеть это до подтверждения.
  const confirmMessage = useMemo(() => {
    const base = `${maskedPhone} станет основным телефоном клиента`;

    if (!currentPhones.phone1) return `${base}.`;

    if (!currentPhones.phone2) {
      return `${base}: текущий основной ${toPhoneText(
        currentPhones.phone1
      )} перейдёт в дополнительный.`;
    }

    return `${base}: текущий основной ${toPhoneText(
      currentPhones.phone1
    )} перейдёт в дополнительный, а дополнительный ${toPhoneText(
      currentPhones.phone2
    )} будет удалён.`;
  }, [currentPhones.phone1, currentPhones.phone2, maskedPhone]);

  const saveToClient = useCallback(async () => {
    if (!clientId || !activeBranchId) return;

    if (!clientBranchId) {
      showNotification(
        'Не удалось определить филиал клиента — номер не записан',
        FRONT_WARNING
      );
      return;
    }

    isSavePendingRef.current = true;
    setIsSaving(true);

    try {
      // Номер становится основным, а основной участвует в поиске дублей —
      // проверяем так же, как это делает форма клиента, чтобы не плодить
      // дубли.
      const checkResult = await checkClientForDuplicate({
        ActiveBranchId: clientBranchId,
        data: { ClientId: clientId, ClientPhone: digits },
      }).unwrap();

      if (checkResult.IsDuplicated) {
        if (!checkResult.IsMergePossible) {
          showNotification(
            'Клиент с таким номером уже есть в филиале, объединить карточки нельзя — номер не записан',
            WARNING
          );
          return;
        }

        // Объединение возможно — решение за пользователем, окно открывает
        // LeadMessagePhone (us/11255).
        setDuplicate(checkResult);
        return;
      }

      await setClientPhoneNumber({
        ActiveBranchId: activeBranchId,
        clientId,
        phoneNumber: digits,
      }).unwrap();

      void refetchLeadCard();

      api?.success({
        message: `Номер ${maskedPhone} записан основным телефоном клиента`,
      });
    } catch (error) {
      showNotification(error, WARNING);
    } finally {
      isSavePendingRef.current = false;
      setIsSaving(false);
    }
  }, [
    activeBranchId,
    api,
    checkClientForDuplicate,
    clientBranchId,
    clientId,
    digits,
    maskedPhone,
    refetchLeadCard,
    setClientPhoneNumber,
    showNotification,
  ]);

  const closeDuplicate = useCallback(() => {
    setDuplicate(null);
  }, []);

  const openExistingClient = useCallback(() => {
    if (!duplicate) return;

    setDuplicate(null);
    navigate(`${CLIENT_CARD_PATH}?id=${duplicate.ExistingClientId}`);
  }, [duplicate, navigate]);

  const mergeWithExistingClient = useCallback(async () => {
    if (!duplicate || !clientId || !clientBranchId) return;

    try {
      const { LeadId } = await mergeClientsAtSameBranch({
        ActiveBranchId: clientBranchId,
        data: {
          ClientId1: clientId,
          ClientId2: duplicate.ExistingClientId,
        },
      }).unwrap();

      setDuplicate(null);
      showNotification('Карточки клиентов объединены', SUCCESS);

      // После объединения лид может смениться — открываем тот, который
      // вернул бэк; если он тот же, хватает перечитать карточку.
      if (LeadId && LeadId !== leadId) {
        navigate(`${LEAD_CARD_PATH}?leadId=${LeadId}`);
        return;
      }

      void refetchLeadCard();
    } catch (error) {
      showNotification(error, WARNING);
    }
  }, [
    clientBranchId,
    clientId,
    duplicate,
    leadId,
    mergeClientsAtSameBranch,
    navigate,
    refetchLeadCard,
    showNotification,
  ]);

  const savePhone = useCallback(() => {
    if (isSavePendingRef.current) return;

    if (!clientId || !activeBranchId) {
      showNotification(
        'Не удалось определить клиента — номер не записан',
        FRONT_WARNING
      );
      return;
    }

    if (isAlreadySaved) {
      showNotification(
        `Номер ${maskedPhone} уже записан у клиента`,
        FRONT_WARNING
      );
      return;
    }

    show({
      dataTestId: 'save-message-phone',
      type: 'info',
      title: 'Записать номер клиенту?',
      message: confirmMessage,
      confirmText: 'Записать',
      cancelText: 'Отмена',
      onConfirm: () => {
        void saveToClient();
      },
    });
  }, [
    activeBranchId,
    clientId,
    confirmMessage,
    isAlreadySaved,
    maskedPhone,
    saveToClient,
    show,
    showNotification,
  ]);

  const hint = useMemo(() => {
    if (isAlreadySaved) return `${maskedPhone} — уже записан у клиента`;

    return `Записать ${maskedPhone} основным телефоном клиента`;
  }, [isAlreadySaved, maskedPhone]);

  return {
    savePhone,
    isSaving,
    hint,
    isAlreadySaved,
    maskedPhone,
    isDuplicateOpen: duplicate !== null,
    closeDuplicate,
    openExistingClient,
    mergeWithExistingClient,
  };
};
